import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import {
  getAccessor,
  getType,
  getWidth,
  isSortable,
  getDefaultValue,
  getMethodName,
  getKeyName,
  getVariables
} from '@nile/client_management/src/Pebbles/InvoiceAuditContainer';
import { DateTimeCell } from '@nile/mangifera/src/Components/Ticket';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  ContentDeciderContainer,
  getTranslatedText,
  TableContainer,
  SkeletonTableLoading,
  useProduct
} from '@nile/components';
import { TicketClient, FETCH_WORKLOG_ENTRIES } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import BooleanCell from '@nile/banyan/src/Components/Tables/BooleanCell';
import EditableCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/EditableCell';
import InvoiceAuditActionCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceAuditActionCell';
import TotalCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/TotalCell';
import CurrencyCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/CurrencyCell';
import ServiceItemCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/ServiceItemCell';
import ProjectAndTicketCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/ProjectAndTicketIdCell';
import ProductHourCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/ProductHourCell';
import InvoiceAuditNotesCell from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceAuditNotesCell';

const ROW_COUNT = 20;

function WorklogEntryDataTable(props) {
  const { match, defaultCondition } = props;
  const { id, sectionId } = match.params;
  const viewId = sectionId || id;
  const currentProduct = useProduct();
  const intl = useIntl();

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);
  const [data, setData] = useState([]);
  const dataRef = useRef(0);
  dataRef.current = data;
  const [listLoading, setListLoading] = useState(true);
  const [listInfo, setListInfo] = useState({});
  const ticketContext = useContext(TicketContext);

  useEffect(() => {
    fetchViewData();
  }, [defaultCondition]);

  const getActions = (col) => {
    switch (col.name) {
      case 'displayId':
        return {
          onClick: (item) => {
            const ticketId = item?.ticket?.displayId;
            const projectId = item?.project?.displayId;

            if (ticketId)
              ticketContext.openTab({
                module: 'TICKET',
                data: item.ticket
              });

            if (projectId) {
              ticketContext.openTab({
                module: 'PROJECT_DETAIL',
                data: item.project
              });
            }
          }
        };
      default:
        return;
    }
  };

  /**
   * Method to get Service items.
   * Call this method to fetch/refetch the data on mounting/sorting/filtering
   * @param {object} personalization
   * @param {object} pageOptions
   */
  const fetchViewData = async (personalization, pageOptions) => {
    setListLoading(true);
    const response = await TicketClient.query({
      query: FETCH_WORKLOG_ENTRIES,
      variables: {
        input: {
          listInfo: pageOptions
            ? {
                ...pageOptions,
                condition: defaultCondition
              }
            : {
                pageSize: 20,
                page: 1,
                sort: [
                  {
                    attribute: 'billDateTime',
                    order: 'DESC'
                  }
                ],
                condition: defaultCondition
              }
        }
      },
      fetchPolicy: 'no-cache'
    });
    setListLoading(false);

    const cols = [
      {
        name: 'billDateTime',
        label: getTranslatedText(intl, 'common.label.date'),
        dataType: 'dateTime'
      },
      {
        name: 'notes',
        label: getTranslatedText(
          intl,
          'timesheet.details.view.table.header.notes'
        )
      },
      {
        name: 'workItem.subject',
        label: getTranslatedText(
          intl,
          'timesheet.details.view.table.header.workItem'
        )
      },
      {
        name: 'technician.name',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.technicianName'
        )
      },
      {
        name: 'workItem.workId',
        label: getTranslatedText(intl, 'workItem.workId')
      },
      {
        name: 'workItem.module',
        label: getTranslatedText(intl, 'taskView.workItem.module')
      },
      {
        name: 'serviceItem.name',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.serviceItem'
        )
      },
      {
        name: 'billable',
        label: getTranslatedText(
          intl,
          'timesheet.details.view.stat.billable.hours'
        ),
        type: 'Boolean'
      },
      {
        name: 'unitPrice',
        label: getTranslatedText(
          intl,
          'client.navigation.invoiceAudit.list.unitPrice'
        )
      },
      {
        name: 'afterHours',
        label: getTranslatedText(
          intl,
          'timesheet.details.view.stat.after.hours'
        ),
        type: 'Boolean'
      },
      {
        name: 'qty',
        label: getTranslatedText(
          intl,
          'timesheet.details.view.table.header.timespent'
        )
      },
      {
        name: 'workItem.client.name',
        label: getTranslatedText(intl, 'common.client')
      },
      {
        name: 'workItem.site.name',
        label: getTranslatedText(intl, 'app.common.site')
      }
    ];

    const iTcolToRemove = (col) => {
      if (currentProduct == 'IT') {
        return col.filter(
          (co) =>
            ![
              'billable',
              'serviceItem.name',
              'afterHours',
              'unitPrice',
              'workItem.client.name'
            ].includes(co.name)
        );
      }
      return col;
    };

    const columns = iTcolToRemove(cols).map((col, idx) => {
      return {
        Header: col.label,
        accessor: getAccessor(col),
        width: getWidth(col),
        type: col?.type || getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !isSortable(col),
        disableFilters: true,
        isEditable: false,
        helpers: {
          getDefaultValue,
          getMethodName,
          getKeyName,
          getVariables
        },
        field: col,
        wrap: col.name === 'notes',
        isBold: col.name === 'revisedAmount',
        alignRight: col.name === 'revisedAmount',
        view: viewId,
        dataType: col.name === 'billDate' ? 'date' : '',
        actions: getActions(col)
      };
    });

    setColumnGroup([{ Header: 'Name', columns }]);
    setData(response?.data?.fetchWorklogEntries?.entries);
    setListInfo(response?.data?.fetchWorklogEntries?.listInfo);
  };

  const defaultColumnCells = {
    Action: InvoiceAuditActionCell,
    Total: TotalCell,
    Currency: CurrencyCell,
    Edit: EditableCell,
    DateTime: DateTimeCell,
    ServiceItem: ServiceItemCell,
    TicketProjectId: ProjectAndTicketCell,
    ProductHour: ProductHourCell,
    Notes: InvoiceAuditNotesCell,
    Boolean: BooleanCell
  };

  const checkRowHighlight = (row) => {
    return row.original.operationStatus === 'PROGRESS';
  };

  const checkDisabledRow = (row) => {
    return row.original.operationStatus === 'PROGRESS';
  };

  return (
    <TableContainer>
      <ContentDeciderContainer
        isLoading={!columnGroup[0]?.columns?.length}
        hasContent
        fullHeight
        mainContent={
          <TableView
            noBulkSelect
            context={{ viewConditions: defaultCondition }}
            columns={columnGroup}
            data={data}
            setData={setData}
            rowCount={ROW_COUNT}
            refreshTable={fetchViewData}
            listInfo={listInfo}
            pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
            pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
            recordCount={listInfo.totalCount}
            defaultColumnCells={defaultColumnCells}
            activePage={listInfo.page || 0}
            isLoading={listLoading}
            fullHeight
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/WorklogTableEmptyState.svg`,
              heading: getTranslatedText(
                intl,
                'worklog.table.empty.state.title'
              ),
              description: getTranslatedText(
                intl,
                'worklog.table.empty.state.description'
              )
            }}
            cardTable={!!sectionId}
            alignStart
            checkRowHighlight={checkRowHighlight}
            checkDisabledRow={checkDisabledRow}
          />
        }
        loadingContent={<SkeletonTableLoading />}
        notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
      />
    </TableContainer>
  );
}

export default withRouter(WorklogEntryDataTable);
